import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Gift, ChevronDown, CheckSquare, Square } from "lucide-react";
import StoreLayout from "../components/StoreLayout";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { getStoredRef } from "../lib/affiliate";

const FALLBACK_LADDER = [
  { min_qty: 3, amount: 1000 },
  { min_qty: 2, amount: 500 },
  { min_qty: 1, amount: 250 },
];

function discountForQty(qty, ladder) {
  for (const tier of ladder) {
    if (qty >= tier.min_qty) return tier.amount;
  }
  return 0;
}

const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+33", label: "🇫🇷 +33" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+86", label: "🇨🇳 +86" },
];

const EMPTY_ADDR = { full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India" };

export default function Checkout() {
  const { items, total } = useCart();
  const { user, loginWithToken } = useAuth();
  const navigate = useNavigate();

  const [ladder, setLadder] = useState(FALLBACK_LADDER);
  useEffect(() => {
    api.get("/promo/discount-ladder")
      .then((r) => Array.isArray(r.data?.ladder) && setLadder(r.data.ladder))
      .catch(() => {});
  }, []);

  const totalUnits = useMemo(() => items.reduce((a, b) => a + b.qty, 0), [items]);
  const discount = useMemo(() => {
    const raw = discountForQty(totalUnits, ladder);
    return Math.min(raw, Math.max(0, total - 1));
  }, [totalUnits, ladder, total]);
  const grandTotal = Math.max(1, total - discount);

  // Shipping address (used by both guest + auth flows)
  const [shipping, setShipping] = useState({
    ...EMPTY_ADDR,
    full_name: user?.name || "",
  });

  // Guest-only fields
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestCountryCode, setGuestCountryCode] = useState("+91");
  const [guestPhone, setGuestPhone] = useState("");
  const [billingSame, setBillingSame] = useState(true);
  const [billing, setBilling] = useState(EMPTY_ADDR);

  const [busy, setBusy] = useState(false);

  // Sync name/phone into shipping for guest
  useEffect(() => {
    if (!user) {
      setShipping((s) => ({ ...s, full_name: guestName, phone: guestPhone }));
    }
  }, [guestName, guestPhone, user]);

  if (items.length === 0) {
    return (
      <StoreLayout>
        <div className="max-w-xl mx-auto py-20 text-center">
          <p className="label-mono text-[#71717A]">// BAG IS EMPTY</p>
          <button onClick={() => navigate("/shop")} className="btn-neon mt-6">GO SHOP</button>
        </div>
      </StoreLayout>
    );
  }

  // Authenticated user checkout (existing flow)
  const submitAuth = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/checkout/session", {
        items,
        shipping,
        origin_url: window.location.origin,
        ref_code: getStoredRef() || null,
      });
      if (res.data.url) window.location.href = res.data.url;
      else toast.error("Checkout failed");
    } catch (err) {
      toast.error("Checkout error", { description: err?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  // Guest checkout
  const submitGuest = async (e) => {
    e.preventDefault();
    if (!guestEmail || !/^\S+@\S+\.\S+$/.test(guestEmail)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!guestPhone || guestPhone.replace(/\D/g, "").length < 6) {
      toast.error("Enter a valid mobile number");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/checkout/guest", {
        name: guestName,
        email: guestEmail,
        country_code: guestCountryCode,
        phone: guestPhone.replace(/\D/g, ""),
        items,
        shipping: { ...shipping, full_name: guestName, phone: guestPhone },
        billing_same: billingSame,
        billing: billingSame ? null : billing,
        origin_url: window.location.origin,
        ref_code: getStoredRef() || null,
      });
      // Auto-login the guest user so order tracking works immediately
      if (res.data.token && res.data.user) {
        loginWithToken(res.data.token, res.data.user);
      }
      if (res.data.url) window.location.href = res.data.url;
      else toast.error("Checkout failed");
    } catch (err) {
      toast.error("Checkout error", { description: err?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  const AddrFields = ({ values, onChange, prefix }) => (
    <div className="grid sm:grid-cols-2 gap-4">
      {[
        ["line1", "ADDRESS LINE 1", "sm:col-span-2"],
        ["line2", "ADDRESS LINE 2 (OPTIONAL)", "sm:col-span-2"],
        ["city", "CITY"],
        ["state", "STATE"],
        ["pincode", "PINCODE / ZIP"],
        ["country", "COUNTRY"],
      ].map(([k, label, cls]) => (
        <input
          key={k}
          required={k !== "line2"}
          value={values[k]}
          onChange={(e) => onChange({ ...values, [k]: e.target.value })}
          placeholder={label}
          className={`cult-input ${cls || ""}`}
          data-testid={`${prefix}-${k}`}
        />
      ))}
    </div>
  );

  const OrderSummary = () => (
    <div className="bg-[#0A0A0A] border border-[#27272A] p-6 h-fit sticky top-24">
      <p className="label-mono text-[#71717A]">// ORDER SUMMARY</p>
      <div className="space-y-3 mt-4">
        {items.map((it) => (
          <div key={`${it.product_id}-${it.size}-${it.color}`} className="flex items-center gap-3 border-b border-[#18181B] pb-3">
            <img src={it.image} alt="" className="w-14 h-14 object-cover flex-shrink-0" />
            <div className="flex-1 text-sm min-w-0">
              <div className="font-display font-bold leading-tight truncate">{it.name}</div>
              <div className="label-mono text-[#71717A] mt-1">{it.size}/{it.color} × {it.qty}</div>
            </div>
            <div className="font-mono-cult text-sm flex-shrink-0">₹{(it.price * it.qty).toFixed(0)}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-[#27272A] space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="label-mono text-[#71717A]">Subtotal · {totalUnits} item{totalUnits !== 1 ? "s" : ""}</span>
          <span className="font-mono-cult">₹{total.toFixed(0)}</span>
        </div>
        {discount > 0 && (
          <div className="flex justify-between" data-testid="checkout-discount-row">
            <span className="label-mono text-[#CFF048] flex items-center gap-1">
              <Gift size={11} /> BULK LOVE · BUY {totalUnits}
            </span>
            <span className="font-mono-cult text-[#CFF048]">− ₹{discount.toFixed(0)}</span>
          </div>
        )}
        <div className="pt-2 border-t border-[#27272A] flex justify-between text-lg">
          <span className="font-display font-bold">TOTAL</span>
          <span className="font-mono-cult text-[#CFF048] font-bold" data-testid="checkout-grand-total">₹{grandTotal.toFixed(0)}</span>
        </div>
      </div>
      <p className="label-mono text-[#71717A] mt-4 text-[10px] text-center">SECURE PAYMENT // STRIPE</p>
    </div>
  );

  // ─── AUTHENTICATED USER ───────────────────────────────────────────────────
  if (user) {
    return (
      <StoreLayout>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <p className="label-mono text-[#71717A]">// CHECKOUT</p>
          <h1 className="font-display font-black text-5xl md:text-6xl uppercase tracking-tight mt-3 mb-10">Shipping & Pay</h1>
          <div className="grid lg:grid-cols-3 gap-10">
            <form onSubmit={submitAuth} className="lg:col-span-2 space-y-4" data-testid="checkout-form">
              <p className="label-mono text-[#CFF048]">// DELIVERY ADDRESS</p>
              {/* Email field — shown read-only, important for confirmations */}
              <div>
                <input
                  readOnly
                  className="cult-input opacity-70 cursor-not-allowed"
                  value={user.email}
                  placeholder="EMAIL"
                  data-testid="auth-email-field"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  ["full_name", "FULL NAME"],
                  ["phone", "PHONE"],
                  ["line1", "ADDRESS LINE 1", "sm:col-span-2"],
                  ["line2", "ADDRESS LINE 2 (OPTIONAL)", "sm:col-span-2"],
                  ["city", "CITY"],
                  ["state", "STATE"],
                  ["pincode", "PINCODE / ZIP"],
                  ["country", "COUNTRY"],
                ].map(([k, label, cls]) => (
                  <input
                    key={k}
                    required={k !== "line2"}
                    value={shipping[k]}
                    onChange={(e) => setShipping({ ...shipping, [k]: e.target.value })}
                    placeholder={label}
                    className={`cult-input ${cls || ""}`}
                    data-testid={`shipping-${k}`}
                  />
                ))}
              </div>
              <div className="pt-6">
                <button type="submit" disabled={busy} className="btn-neon w-full" data-testid="pay-now-btn">
                  {busy ? "REDIRECTING..." : <><ArrowRight size={18} className="mr-2" /> PAY NOW WITH STRIPE</>}
                </button>
              </div>
            </form>
            <OrderSummary />
          </div>
        </div>
      </StoreLayout>
    );
  }

  // ─── GUEST CHECKOUT ───────────────────────────────────────────────────────
  return (
    <StoreLayout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <p className="label-mono text-[#71717A]">// CHECKOUT</p>
        <h1 className="font-display font-black text-5xl md:text-6xl uppercase tracking-tight mt-3 mb-2">Ship & Pay</h1>
        <p className="text-[#71717A] text-sm mb-10 label-mono">
          // NO SIGN-IN NEEDED · WE'LL CREATE YOUR ACCOUNT & EMAIL YOUR LOGIN CODE
        </p>

        <div className="grid lg:grid-cols-3 gap-10">
          <form onSubmit={submitGuest} className="lg:col-span-2 space-y-6" data-testid="guest-checkout-form">

              {/* SECTION 1: CONTACT INFO */}
              <div>
                <p className="label-mono text-[#CFF048] mb-4">// 01 · YOUR DETAILS</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    required
                    className="cult-input"
                    placeholder="FULL NAME"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    data-testid="guest-name"
                  />
                  <input
                    required
                    type="email"
                    className="cult-input"
                    placeholder="EMAIL ADDRESS (BECOMES YOUR LOGIN ID)"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    data-testid="guest-email"
                  />
                  <div className="relative">
                    <select
                      value={guestCountryCode}
                      onChange={(e) => setGuestCountryCode(e.target.value)}
                      className="cult-input pr-8 appearance-none w-full"
                      data-testid="guest-country-code"
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] pointer-events-none" />
                  </div>
                  <input
                    required
                    type="tel"
                    inputMode="numeric"
                    className="cult-input"
                    placeholder="MOBILE NUMBER"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value.replace(/\D/g, "").slice(0, 15))}
                    data-testid="guest-phone"
                  />
                </div>
                <div className="border border-[#27272A] bg-[#0A0A0A] p-3 mt-3">
                  <p className="label-mono text-[#71717A] text-[10px]">
                    // AN OTP WILL BE SENT TO YOUR EMAIL · USE IT TO CHECK YOUR ORDER HISTORY & TRACK YOUR PACKAGE
                  </p>
                </div>
              </div>

            {/* SECTION 2: DELIVERY ADDRESS */}
            <div>
              <p className="label-mono text-[#CFF048] mb-4">// 02 · DELIVERY ADDRESS</p>
              <AddrFields values={shipping} onChange={setShipping} prefix="shipping" />
            </div>

            {/* SECTION 3: BILLING ADDRESS */}
            <div>
              <p className="label-mono text-[#CFF048] mb-4">// 03 · BILLING ADDRESS</p>
              <button
                type="button"
                onClick={() => setBillingSame(!billingSame)}
                className="flex items-center gap-2 label-mono text-sm mb-4"
                data-testid="billing-same-toggle"
              >
                {billingSame ? (
                  <CheckSquare size={18} className="text-[#CFF048]" />
                ) : (
                  <Square size={18} className="text-[#71717A]" />
                )}
                SAME AS DELIVERY ADDRESS
              </button>
              {!billingSame && (
                <AddrFields values={billing} onChange={setBilling} prefix="billing" />
              )}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={busy}
                className="btn-neon w-full text-base py-4"
                data-testid="guest-pay-btn"
              >
                {busy ? "CREATING ACCOUNT & REDIRECTING..." : <><ArrowRight size={18} className="mr-2" /> PLACE ORDER & PAY WITH STRIPE</>}
              </button>
              <p className="label-mono text-[#71717A] mt-3 text-center text-[10px]">
                // SECURE PAYMENT VIA STRIPE · YOUR DATA IS SAFE
              </p>
            </div>
          </form>

          <OrderSummary />
        </div>
      </div>
    </StoreLayout>
  );
}
